import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { AlertTriangle, X } from "lucide-react";

/**
 * Non-blocking data-quality guardrail. When a Process Manager works with an
 * operational BIA while their org has NOT activated BIA Configuration (impact
 * definitions still fall back to the system default), show a dismissible
 * warning. Activation signal: GET /api/impact-definitions/ returns is_default
 * === false once an org-specific config (version >= 1) has been saved/activated.
 * This only warns — it never blocks creation and never changes scoring.
 */
export const BiaConfigWarningBanner = () => {
  const { api, user } = useAuth();
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let active = true;
    if (user?.role !== "process_manager") return;
    (async () => {
      try {
        const res = await api.get("/impact-definitions/");
        if (active && res?.data?.is_default === true) setShow(true);
      } catch (_) {
        /* guardrail is best-effort — never surface an error to the user */
      }
    })();
    return () => {
      active = false;
    };
  }, [api, user]);

  if (!show || dismissed) return null;

  return (
    <div
      data-testid="bia-config-warning-banner"
      className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900"
    >
      <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0 text-amber-600" />
      <p className="text-sm flex-1">
        <strong>BIA Configuration is not activated.</strong> Impact scales fall back to
        defaults and criticality/RTO results may be inaccurate. Ask your BCM Manager to
        complete and Activate BIA Configuration first.
      </p>
      <button
        data-testid="bia-config-warning-dismiss"
        onClick={() => setDismissed(true)}
        className="text-amber-600 hover:text-amber-800 flex-shrink-0"
        aria-label="Dismiss warning"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default BiaConfigWarningBanner;
